import { type BlockConfig, iconField } from './_types';

/**
 * Payload `Block` config sidecar for the `b2bIntegrations` block.
 *
 * Decomposed from `src/components/b2b/B2BIntegrations.tsx`. Two-column
 * layout with Tabs-based code samples on the left and integration cards
 * on the right.
 *
 * Slug + interfaceName naming reconciliation:
 *   File name:               `b2b-integrations.tsx` / `b2b-integrations.payload.ts`
 *   React component export:  `B2BIntegrations`
 *   Payload `slug`:          `b2bIntegrations`
 *   Payload `interfaceName`: `B2BIntegrationsBlock`
 */

export const B2BIntegrationsBlock: BlockConfig = {
  slug: 'b2bIntegrations',
  interfaceName: 'B2BIntegrationsBlock',
  labels: {
    singular: 'B2B Integrations',
    plural: 'B2B Integrations Blocks',
  },
  fields: [
    {
      name: 'eyebrow',
      type: 'text',
      label: 'Eyebrow',
      defaultValue: 'Integrations',
    },
    {
      name: 'headline',
      type: 'textarea',
      required: true,
      label: 'Headline',
      admin: { rows: 2 },
    },
    {
      name: 'subheadline',
      type: 'textarea',
      label: 'Subheadline',
      admin: { rows: 2 },
    },
    {
      name: 'codeExamples',
      type: 'array',
      required: true,
      minRows: 1,
      maxRows: 6,
      label: 'Code examples (left column)',
      labels: { singular: 'Example', plural: 'Examples' },
      fields: [
        {
          name: 'language',
          type: 'text',
          required: true,
          label: 'Language key',
          admin: {
            description:
              'Used as the Tab value (e.g. "curl", "python", "javascript"). Must be unique within this block.',
          },
        },
        {
          name: 'label',
          type: 'text',
          required: true,
          label: 'Tab label',
          admin: {
            description:
              'Displayed on the tab trigger (e.g. "cURL", "Python", "JavaScript").',
          },
        },
        {
          name: 'code',
          type: 'textarea',
          required: true,
          label: 'Code body',
          admin: { rows: 12 },
        },
      ],
    },
    {
      name: 'rightColumnHeading',
      type: 'text',
      label: 'Right column heading',
      defaultValue: 'Multiple Integration Paths',
    },
    {
      name: 'rightColumnBody',
      type: 'textarea',
      label: 'Right column body',
      admin: { rows: 3 },
    },
    {
      name: 'integrations',
      type: 'array',
      required: true,
      minRows: 1,
      maxRows: 8,
      label: 'Integration cards (right column)',
      labels: { singular: 'Integration', plural: 'Integrations' },
      fields: [
        iconField({
          name: 'icon',
          label: 'Icon (lucide-react name)',
          required: true,
        }),
        { name: 'name', type: 'text', required: true, label: 'Name' },
        {
          name: 'description',
          type: 'text',
          required: true,
          label: 'Description',
        },
      ],
    },
    {
      name: 'footnoteHeading',
      type: 'text',
      label: 'Footnote heading',
    },
    {
      name: 'footnoteBody',
      type: 'textarea',
      label: 'Footnote body',
      admin: { rows: 2 },
    },
  ],
};

export default B2BIntegrationsBlock;
